import { z } from "zod";
import { DbConversationId, UserId, ConversationId } from "@/types/branded";
import { conversationQuestionSchema } from "@/types/schemas/core";

// Timestamp schema for consistent date handling
const isoTimestamp = z.string().datetime();
const adminPermissionSchema = z.enum(["quota_admin"]);

// Database conversation schema
export const dbConversationSchema = z.object({
  id: z.number().transform(DbConversationId),
  userId: z.number().transform(UserId),
  dustConversationId: z.string().transform(ConversationId),
  title: z.string().nullable(),
  shareToken: z.string().uuid().nullable().optional(),
  isPublic: z.boolean().optional().default(false),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
  startedAt: isoTimestamp,
  lastActivityAt: isoTimestamp,
  questions: z.array(conversationQuestionSchema).default([]),
  questionsSyncedAt: isoTimestamp.nullable().optional(),
});

// Database conversation label schema
export const dbConversationLabelSchema = z.object({
  id: z.number(),
  conversationId: z.number(),
  labelType: z.enum(["documentType", "userTag"]),
  tagId: z.string(),
  label: z.string(),
  color: z.string(),
  createdAt: isoTimestamp,
});

// Reference table: Document types
export const dbDocumentTypeSchema = z.object({
  id: z.number(),
  tagId: z.string(),
  label: z.string(),
  color: z.string(),
  createdAt: isoTimestamp,
});

// Reference table: Default user tags (template for new users)
export const dbDefaultUserTagSchema = z.object({
  id: z.number(),
  tagId: z.string(),
  label: z.string(),
  color: z.string(),
  createdAt: isoTimestamp,
});

// Database user schema
export const dbUserSchema = z.object({
  id: z.number(),
  email: z.string(),
  externalUserGuid: z.string().nullable(),
  cguAcceptedVersion: z.string().nullable().optional(),
  adminPermissions: z.array(adminPermissionSchema).default([]),
  /** Version des CGU exigée par le serveur (identique à `GET /api/cgu`). */
  requiredCguVersion: z.string(),
  createdAt: isoTimestamp,
  updatedAt: isoTimestamp,
});

// Frontend conversation metadata schema
export const conversationMetadataSchema = z.object({
  id: z.string(), // dustConversationId (normalized to string)
  dbId: z.number().optional(), // Database ID (used for API calls like labels)
  title: z.string(),
  startedAt: z.string().optional(),
  lastActivityAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  documentTypes: z.array(z.string()).default([]), // tagIds from ref_document_types
  customTags: z.array(z.string()).default([]), // tagIds from user_tags
  questions: z.array(conversationQuestionSchema).optional(), // Cached list of all user questions
});

// One conversation's questions (for global TanStack DB collection)
export const conversationQuestionsItemSchema = z.object({
  conversationId: z.string(),
  questions: z.array(conversationQuestionSchema),
});
export type ConversationQuestionsItem = z.infer<
  typeof conversationQuestionsItemSchema
>;

// Assistant schema (from db/schemas.ts)
export const assistantSchema = z.object({
  id: z.string(), // sId
  label: z.string(), // name
  description: z.string().optional(),
  avatarUrl: z.string().optional(),
  status: z.string(),
});

// Conversation schema (from db/schemas.ts)
// All temporal fields are ISO strings to match the backend API format and
// avoid Date-object serialisation issues in TanStack DB mutation validation.
export const conversationSchema = z.object({
  id: z.string(), // dustConversationId (normalized to string)
  dbId: z.number().optional(), // Database ID for API calls
  title: z.string(),
  startedAt: z.string().optional(),
  lastActivityAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedAt: z.string().optional(),
  documentTypes: z.array(z.string()).default([]),
  customTags: z.array(z.string()).default([]),
  shareToken: z.string().uuid().nullable().optional(),
  isPublic: z.boolean().optional().default(false),
});

// Réponse de l'API lors d'un partage réussi
export const shareConversationResponseSchema = z.object({
  shareToken: z.string().uuid(),
  isPublic: z.literal(true),
  shareUrl: z.string().url(),
});

export type ShareConversationResponse = z.infer<
  typeof shareConversationResponseSchema
>;

// Payload de la vue publique d'une conversation partagée
export const sharedConversationSchema = z.object({
  id: z.string(), // dustConversationId
  dbId: z.number(),
  title: z.string().nullable(),
  createdAt: z.string(),
  startedAt: z.string(),
  lastActivityAt: z.string(),
  content: z.array(z.unknown()),
});

export type SharedConversation = z.infer<typeof sharedConversationSchema>;

// User preferences schema (from db/schemas.ts)
export const userPreferencesSchema = z.object({
  key: z.literal("user-preferences"),
  currentConversationId: z.string().nullable(),
  showChainOfThought: z.boolean(),
  theme: z.enum(["light", "dark"]),
  sidebarWidth: z.number().optional(),
  suggestionResponseVisible: z
    .record(z.string(), z.record(z.string(), z.boolean()))
    .optional(),
  suggestionResponseGateDismissed: z.boolean().optional(),
  conversationListCollapse: z
    .object({
      groups: z.array(z.string()),
      leaves: z.array(z.string()),
    })
    .optional(),
});

// Viewport schema (UI state - in-memory)
export const viewportSchema = z.object({
  key: z.literal("viewport"),
  isMobile: z.boolean(),
});

// Inferred types
export type DbConversation = z.infer<typeof dbConversationSchema>;
export type DbConversationLabel = z.infer<typeof dbConversationLabelSchema>;
export type DbDocumentType = z.infer<typeof dbDocumentTypeSchema>;
export type DbDefaultUserTag = z.infer<typeof dbDefaultUserTagSchema>;
export type DbUser = z.infer<typeof dbUserSchema>;
export type ConversationMetadata = z.infer<typeof conversationMetadataSchema>;
export type Assistant = z.infer<typeof assistantSchema>;
export type Conversation = z.infer<typeof conversationSchema>;
export type UserPreferences = z.infer<typeof userPreferencesSchema>;
